import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDevMode } from '../contexts/DevModeContext';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  LayoutDashboard, 
  FolderKanban, 
  CheckSquare, 
  Clock, 
  Bell,
  Users,
  User,
  DollarSign,
  Settings,
  LogOut,
  Briefcase,
  Moon,
  Sun,
  Code,
  AlertTriangle,
  Database,
  Upload,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { getUserAvatar } from '../utils/helpers';
import TimeTracker from './TimeTracker';

const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { devMode, toggleDevMode, apiCallCount } = useDevMode();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  // If on central dashboard, render children without layout
  if (location.pathname === '/central') {
    return <>{children}</>;
  }

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['all'], excludeRoles: ['supplier'] },
    { name: 'My Projects', href: '/my-projects', icon: FolderKanban, roles: ['all'], excludeRoles: ['supplier'] },
    { name: 'All Projects', href: '/projects', icon: Briefcase, roles: ['all'], excludeRoles: ['supplier'] },
    { name: 'My Tasks', href: '/my-tasks', icon: CheckSquare, roles: ['all'], excludeRoles: ['supplier'] },
    { name: 'My Hours', href: '/my-hours', icon: Clock, roles: ['all'], excludeRoles: ['supplier'] },
    { name: 'All Tasks', href: '/tasks', icon: CheckSquare, roles: ['Admin', 'PMO'] },
    { name: 'All Hours', href: '/hours', icon: Clock, roles: ['Admin', 'PMO'] },
    { name: 'Reminders', href: '/reminders', icon: Bell, roles: ['all'], excludeRoles: ['supplier'] },
    { name: 'QC Incidents', href: '/qc-incidents', icon: AlertTriangle, roles: ['all'] },
  ];

  const adminNavigation = [
    { name: 'Finance Admin', href: '/admin/finance', icon: DollarSign, roles: ['Finance', 'Admin'] },
    { 
      name: 'Airports Division', 
      href: '/admin/airports', 
      icon: Settings, 
      roles: ['Airports Division Manager', 'Admin'],
      subItems: [
        { name: 'PM View', href: '/admin/airports/pm-view', roles: ['Airports Division Manager', 'Admin'] }
      ]
    },
    { name: 'Business Development', href: '/admin/business-dev', icon: Users, roles: ['Business Development', 'Admin'] },
    { name: 'PMO Dashboard', href: '/admin/pmo', icon: Settings, roles: ['PMO', 'Admin'] },
    { name: 'Project Settings', href: '/settings', icon: Settings, roles: ['Admin'] },
    { name: 'Global Settings', href: '/admin/settings', icon: Settings, roles: ['Admin'] },
    { name: 'Import QC Incidents', href: '/admin/qc-import', icon: Database, roles: ['Admin'] },
  ];

  const canAccessRoute = (route) => {
    // Check if user's role is in exclude list
    if (route.excludeRoles && route.excludeRoles.some(role => hasRole(role))) {
      return false;
    }
    
    if (route.roles.includes('all')) return true;
    return route.roles.some(role => hasRole(role));
  };

  const visibleNavigation = navigation.filter(canAccessRoute);
  const visibleAdminNavigation = adminNavigation.filter(canAccessRoute);

  const isActive = (path) => location.pathname === path;
  
  // Check if user is supplier
  const isSupplier = user?.role?.toLowerCase() === 'supplier' || 
                     user?.roles?.some(r => r.toLowerCase().includes('supplier'));
  
  console.log('Layout - User:', user);
  console.log('Layout - isSupplier:', isSupplier);
  console.log('Layout - user.role:', user?.role);
  console.log('Layout - user.roles:', user?.roles);

  const UserAvatar = ({ size = 'md' }) => {
    if (!user) return null;
    
    const avatar = getUserAvatar(user);
    const sizeClasses = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12'
    };

    if (avatar.type === 'image') {
      return (
        <>
          <img
            src={avatar.src}
            alt={user.name}
            className={`${sizeClasses[size]} rounded-full object-cover border-2 border-gray-200 dark:border-gray-700`}
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div 
            className={`${sizeClasses[size]} rounded-full ${avatar.color} flex items-center justify-center text-white font-medium border-2 border-gray-200 dark:border-gray-700`}
            style={{ display: 'none' }}
          >
            {avatar.initials}
          </div>
        </>
      );
    }

    return (
      <div className={`${sizeClasses[size]} rounded-full ${avatar.color} flex items-center justify-center text-white font-medium border-2 border-gray-200 dark:border-gray-700`}>
        {avatar.initials}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button - LEFT SIDE */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 dark:text-white" /> : <Menu className="w-6 h-6 dark:text-white" />}
            </button>
            
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Velocity PM</h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Time Tracker - Hidden from suppliers */}
            {!isSupplier && <TimeTracker />}
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </div>
        </div>
        
        {/* API Call Counter */}
        {!devMode && (
          <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between text-xs">
              <span className="text-blue-700 dark:text-blue-300">API Calls Today:</span>
              <span className={`font-bold ${apiCallCount > 800 ? 'text-red-600 dark:text-red-400' : 'text-blue-700 dark:text-blue-300'}`}>
                {apiCallCount} / ~1000
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Velocity PM System</h1>
            </div>
            
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {visibleNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition ${
                      isActive(item.href)
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                );
              })}

              {visibleAdminNavigation.length > 0 && (
                <>
                  <div className="pt-4 pb-2 px-3">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Administration
                    </h3>
                  </div>
                  {visibleAdminNavigation.map((item) => {
                    const Icon = item.icon;
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isExpanded = expandedMenus[item.name];
                    const isItemActive = isActive(item.href) || (hasSubItems && item.subItems.some(sub => isActive(sub.href)));
                    
                    return (
                      <div key={item.name}>
                        {hasSubItems ? (
                          <>
                            <button
                              onClick={() => setExpandedMenus(prev => ({ ...prev, [item.name]: !prev[item.name] }))}
                              className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition ${
                                isItemActive
                                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center">
                                <Icon className="w-5 h-5 mr-3" />
                                {item.name}
                              </div>
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            {isExpanded && (
                              <div className="ml-8 mt-1 space-y-1">
                                <Link
                                  to={item.href}
                                  className={`block px-3 py-2 text-sm rounded-lg transition ${
                                    isActive(item.href)
                                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  Main View
                                </Link>
                                {item.subItems.map((subItem) => (
                                  canAccessRoute(subItem) && (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.href}
                                      className={`block px-3 py-2 text-sm rounded-lg transition ${
                                        isActive(subItem.href)
                                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      {subItem.name}
                                    </Link>
                                  )
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <Link
                            to={item.href}
                            className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition ${
                              isActive(item.href)
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Icon className="w-5 h-5 mr-3" />
                            {item.name}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </nav>

            <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
              {/* User Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <UserAvatar size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Link
                    to="/profile"
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="My Profile"
                  >
                    <User className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={toggleTheme}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  >
                    {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={logout}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-gray-800 bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
            <aside className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <nav className="px-2 py-4 space-y-1">
                {visibleNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition ${
                        isActive(item.href)
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}

                {visibleAdminNavigation.length > 0 && (
                  <>
                    <div className="pt-4 pb-2 px-3">
                      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Administration
                      </h3>
                    </div>
                    {visibleAdminNavigation.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition ${
                            isActive(item.href)
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Icon className="w-5 h-5 mr-3" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </>
                )}
              </nav>

              <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <UserAvatar size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/profile"
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="My Profile"
                    >
                      <User className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={logout}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 lg:pl-64">
          {/* Desktop Top Bar with Time Tracker */}
          <div className="hidden lg:flex items-center justify-end px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
            {!isSupplier && (
              <div className="flex items-center gap-3">
                <TimeTracker />
              </div>
            )}
          </div>
          
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
